import {
  Alert,
  AlertTitle,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { Box } from "@mui/system";
import { Formik, getIn } from "formik";
import { forwardRef } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import ContactInput, { ContactData } from "@/common/components/Contact/ContactInput";
import FoldableBlock from "@/common/components/Display/FoldableBlock";
import GeneralAddressInput from "@/common/components/Entity/General/GeneralAddress/GeneralAddressInput";
import AppIcon from "@/common/components/Icons/AppIcon";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { CustomerHelper } from "@/common/helpers/entity/customer";
import { IdHelper } from "@/common/helpers/id";
import { RegexHelper } from "@/common/helpers/regex";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { cast } from "@/common/ts/conversions";
import { BaseEntityDefaultValues } from "@/common/ts/entity";
import { BaseFormikValues, ValidationErrors } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  CustomerContactCreateDto,
  CustomerContactDto,
  CustomerContactUpdateDto,
  CustomerCreateDto,
  CustomerDto,
  CustomerType,
  CustomerUpdateDto,
  EntityType,
} from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FormActions from "../../Form/FormActions";
import AppTextArea from "../../Form/Input/AppTextArea";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateExtras } from "../components/BaseEntityCreateUpdateExtras";

export type CustomerDefaultValues = BaseEntityDefaultValues & {
  nameOrEmail?: string;
  type?: CustomerCreateDto["type"];
  name?: CustomerCreateDto["name"];
  notes?: CustomerCreateDto["notes"];
  address?: CustomerCreateDto["address"];
  contact?: CustomerContactCreateDto;
};

type ContactValue = CustomerContactCreateDto &
  CustomerContactUpdateDto &
  Pick<CustomerContactDto, "isLinkedToExternalUser"> & { tempId?: string };

export type CustomerCreateUpdateHandle = FormWrapperHandle;

export interface CustomerCreateUpdateProps extends BaseEntityCreateUpdateInheritableProps<
  CustomerDto,
  CustomerDefaultValues
> {
  customerId?: string;
}

export default forwardRef<CustomerCreateUpdateHandle, CustomerCreateUpdateProps>(
  function CustomerCreateUpdate(
    { customerId, defaultValues, onCreate, onUpdate, onSave }: CustomerCreateUpdateProps,
    ref,
  ) {
    const mounted = useMounted();
    const { enqueueSnackbar } = useAppSnackbar();
    const isCreate = !customerId;
    const isEdit = !!customerId;

    const customerRequest = useApiRequest(
      apiClient.customersApi.apiV1CustomersCustomerIdGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        customerId: customerId!,
      },
      {
        skip: !customerId,
      },
    );
    const customer = customerRequest?.data;

    useBreadcrumbReplacements({
      waitTimeout: 10_000,
      idBreadcrumb: customer && {
        idValue: customer.id!,
        newTitle: customer.name || "",
      },
    });

    return (
      <BaseEntityCreateUpdate
        entityType={EntityType.Customer}
        entityId={customerId}
        entity={customer}
        entityRequest={customerRequest}
      >
        <Stack spacing={2}>
          {customerRequest.isLoading && <LinearProgress />}

          <Formik<
            Omit<CustomerCreateDto, "contacts"> &
              Omit<CustomerUpdateDto, "contacts"> &
              BaseFormikValues & {
                contacts?: ContactValue[];
              }
          >
            enableReinitialize={false}
            initialValues={{
              type: defaultValues?.type || customer?.type || CustomerType.B2C,
              name: defaultValues?.name || customer?.name || "",
              notes: defaultValues?.notes || customer?.notes || "",
              departmentIds: customer?.departmentIds || undefined,
              locationIds: customer?.locationIds || undefined,
              contacts: defaultValues?.contact
                ? [
                    {
                      ...defaultValues?.contact,
                      id: undefined,
                      tempId: IdHelper.newUuid4(),
                    },
                  ]
                : customer?.contacts
                  ? customer?.contacts.map((x) => ({
                      ...x,
                      id: x.id,
                      tempId: x.id || IdHelper.newUuid4(),
                      personName: {
                        firstName: x.personName?.firstName || undefined,
                        lastName: x.personName?.lastName || undefined,
                      },
                      email: x.email,
                      isPrimary: x.isPrimary,
                      phoneNumber: x.phoneNumber,
                      nationalIdentityNumber: x.nationalIdentityNumber,
                    }))
                  : [
                      {
                        id: undefined,
                        tempId: IdHelper.newUuid4(),
                        personName: undefined,
                        email:
                          (defaultValues?.nameOrEmail &&
                            RegexHelper.isEmail(defaultValues.nameOrEmail) &&
                            defaultValues?.nameOrEmail) ||
                          undefined,
                        phoneNumber: undefined,
                        nationalIdentityNumber: undefined,
                        isPrimary: true,
                      },
                    ],
              submit: "",
              isDriver: true,
              address:
                defaultValues?.address ||
                (customer?.address && {
                  country: customer?.address?.country || undefined,
                  state: customer?.address?.state || undefined,
                  city: customer?.address?.city || undefined,
                  line1: customer?.address?.line1 || undefined,
                  line2: customer?.address?.line2 || undefined,
                  postalCode: customer?.address?.postalCode || undefined,
                }) ||
                undefined,
              tags: customer?.tags || undefined,
            }}
            validationSchema={Yup.object().shape({})}
            onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
              const values2 = {
                ...values,
                contacts: values.contacts?.map((x) => ({
                  ...x,
                  nationalIdentityNumber:
                    x.nationalIdentityNumber?.number && x.nationalIdentityNumber?.countryCode
                      ? x.nationalIdentityNumber
                      : undefined,
                })),
              };
              try {
                if (isCreate) {
                  const response = await apiClient.customersApi.apiV1CustomersPost({
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    customerCreateDto: {
                      ...values2,
                    },
                  });
                  enqueueSnackbar("Customer created.", { variant: "success" });
                  onCreate && onCreate(response.data);
                  onSave && onSave(response.data);
                } else {
                  const response = await apiClient.customersApi.apiV1CustomersCustomerIdPut({
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    customerId,
                    customerUpdateDto: {
                      ...values2,
                    },
                  });
                  enqueueSnackbar("Customer updated.", { variant: "success" });
                  onUpdate && onUpdate(response.data);
                  onSave && onSave(response.data);
                }

                if (mounted.current) {
                  setStatus({ success: true });
                  setSubmitting(false);
                }
              } catch (err: any) {
                if (mounted.current) {
                  ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                  setStatus({ success: false });
                  setSubmitting(false);
                }
              }
            }}
          >
            {(formikProps) => {
              const {
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                touched,
                values,
                setFieldValue,
                setValues,
              } = formikProps;

              const isMaxContacts = (values.contacts?.length ?? 0) >= CustomerHelper.maxContacts;

              return (
                <form noValidate onSubmit={handleSubmit}>
                  <BaseEntityCreateUpdateExtras
                    defaultImperativeHandle={{
                      formWrapperRef: ref,
                      formikProps: formikProps,
                    }}
                    guardProps={{
                      formikProps: formikProps,
                    }}
                  />

                  <FormContentBlock>
                    <Box>
                      {/* Tags */}
                      <FormControl margin='dense' fullWidth>
                        <GeneralAttachedTagsInput
                          value={values.tags}
                          onChange={(newValue) => {
                            setFieldValue("tags", newValue);
                          }}
                        />
                        <FormHelperText error>
                          {ValidationHelper.getFormikErrorsAsString(errors.tags, {
                            isIncludeNested: false,
                          })}
                        </FormHelperText>
                      </FormControl>
                    </Box>

                    <FormControl
                      margin='normal'
                      fullWidth
                      disabled={!isCreate}
                      error={Boolean(touched.type && errors.type)}
                    >
                      <InputLabel required>Customer type</InputLabel>
                      <Select
                        label='Customer type'
                        displayEmpty={false}
                        name='type'
                        value={values.type}
                        onChange={(e) => setFieldValue("type", e.target.value as CustomerType)}
                      >
                        <MenuItem value=''>
                          <em>None</em>
                        </MenuItem>
                        {Object.values(CustomerType)
                          .filter((x) => x !== CustomerType.None)
                          .map((x, index) => (
                            <MenuItem key={index} value={x}>
                              {x}
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>{touched.type && errors.type}</FormHelperText>
                    </FormControl>

                    {values.type !== CustomerType.B2C && (
                      <TextField
                        error={Boolean(
                          values.type === CustomerType.B2B && touched.name && errors.name,
                        )}
                        helperText={values.type === CustomerType.B2B && touched.name && errors.name}
                        fullWidth
                        required
                        label='Company name'
                        margin='normal'
                        name='name'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type='text'
                        value={values.name}
                        variant='outlined'
                      />
                    )}

                    {/* Entity affiliation */}
                    <EntityAffiliationInputV2
                      departments={{
                        departmentIds: values.departmentIds,
                        onChange: (departmentIds) => {
                          setFieldValue("departmentIds", departmentIds);
                        },
                        error: errors.departmentIds,
                      }}
                      locations={{
                        locationIds: values.locationIds,
                        onChange: (locationIds) => {
                          setFieldValue("locationIds", locationIds);
                        },
                        error: errors.locationIds,
                      }}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isDriver!}
                          onChange={(e) => setFieldValue("isDriver", e.target.checked)}
                        />
                      }
                      label={
                        <Typography component='span' sx={{ display: "flex" }}>
                          Is driver
                        </Typography>
                      }
                    />

                    <Box sx={{ mt: 1 }}>
                      <Typography component='div' variant='h6'>
                        Contacts *
                      </Typography>

                      {isEdit && (
                        <Alert severity='warning' sx={{ my: 2 }}>
                          <AlertTitle>Keep in mind!</AlertTitle>
                          <ul>
                            <li>
                              When contact email is updated, the customer user with old email is
                              suspended.
                            </li>
                            <li>
                              When contact is deleted, the customer user with that email is
                              suspended.
                            </li>
                            <li>
                              When email of existing suspended customer user is used for a contact,
                              that user is resumed.
                            </li>
                          </ul>
                        </Alert>
                      )}

                      <Stack
                        direction={{ xxs: "column", md: "row" }}
                        gap={1}
                        sx={{ mt: 1, flexWrap: "wrap" }}
                      >
                        {values.contacts?.map((contact, i) => (
                          <Box key={contact.id || i}>
                            <ContactInput
                              sx={{ mb: 1 }}
                              value={{
                                id: contact.id || undefined,
                                tempId: contact.tempId || undefined,
                                firstName: contact.personName?.firstName,
                                lastName: contact.personName?.lastName,
                                email: contact.email,
                                phoneNumber: contact.phoneNumber,
                                nationalIdentityNumber: contact.nationalIdentityNumber,
                                isPrimary: contact.isPrimary,
                              }}
                              displayProps={{
                                firstName: true,
                                lastName: true,
                                email: true,
                                phoneNumber: true,
                                nationalIdentityNumber: true,
                                isPrimary: true,
                              }}
                              inputsProps={{
                                firstName: {
                                  name: `contacts[${i}].personName.firstName`,
                                  error: Boolean(
                                    getIn(errors, `contacts[${i}].personName.firstName`),
                                  ),
                                  helperText:
                                    getIn(errors, `contacts[${i}].personName.firstName`) &&
                                    ValidationHelper.getErrorsAsString(
                                      getIn(errors, `contacts[${i}].personName.firstName`),
                                    ),
                                  onBlur: handleBlur,
                                },
                                lastName: {
                                  name: `contacts[${i}].personName.lastName`,
                                  error: Boolean(
                                    getIn(errors, `contacts[${i}].personName.lastName`),
                                  ),
                                  helperText:
                                    getIn(errors, `contacts[${i}].personName.lastName`) &&
                                    ValidationHelper.getErrorsAsString(
                                      getIn(errors, `contacts[${i}].personName.lastName`),
                                    ),
                                  onBlur: handleBlur,
                                },
                                email: {
                                  name: `contacts[${i}].email`,
                                  disabled: contact.isLinkedToExternalUser,
                                  onBlur: handleBlur,
                                },
                                phoneNumber: {
                                  name: `contacts[${i}].phoneNumber`,
                                  disabled: contact.isLinkedToExternalUser,
                                  onBlur: handleBlur,
                                },
                                nationalIdentityNumber: {
                                  name: `contacts[${i}].nationalIdentityNumber`,
                                  onBlur: handleBlur,
                                },
                                isPrimary: {
                                  name: `contacts[${i}].isPrimary`,
                                  disabled:
                                    contact.isLinkedToExternalUser ||
                                    values.contacts?.some((x) => x.isLinkedToExternalUser),
                                  onBlur: handleBlur,
                                },
                              }}
                              errors={
                                getIn(errors, `contacts[${i}]`) as ValidationErrors<ContactData>
                              }
                              action={
                                <AppIconButton
                                  disabled={contact.isLinkedToExternalUser}
                                  onClick={() => {
                                    setValues((currentValues) => ({
                                      ...currentValues,
                                      contacts: currentValues.contacts?.filter(
                                        (x) => x.tempId !== contact.tempId,
                                      ),
                                    }));
                                  }}
                                >
                                  <AppIcon of='close' />
                                </AppIconButton>
                              }
                              secondaryIcon={
                                contact.isLinkedToExternalUser
                                  ? (props) => (
                                      <AppIcon
                                        {...props}
                                        of='warning'
                                        color='warning'
                                        tooltipTitle={
                                          "This contact is linked to an external user. Certain fields are read-only."
                                        }
                                      />
                                    )
                                  : undefined
                              }
                              onChange={(newValue) => {
                                setValues((currentValues) => ({
                                  ...currentValues,
                                  contacts: currentValues.contacts?.map((x) =>
                                    x.tempId === newValue?.tempId
                                      ? {
                                          ...x,
                                          ...newValue,
                                          personName: {
                                            firstName: newValue?.firstName,
                                            lastName: newValue?.lastName,
                                          },
                                        }
                                      : {
                                          ...x,
                                          isPrimary: newValue.isPrimary ? false : x.isPrimary,
                                        },
                                  ),
                                }));
                              }}
                            />
                          </Box>
                        ))}
                      </Stack>

                      {errors.contacts &&
                        ValidationHelper.getFormikErrorsAsString(errors.contacts, {
                          isIncludeNested: false,
                        }) && (
                          <FormHelperText error>
                            {ValidationHelper.getFormikErrorsAsString(errors.contacts, {
                              isIncludeNested: false,
                            })}
                          </FormHelperText>
                        )}

                      <Box sx={{ mt: 1, width: "fit-content" }}>
                        <Button
                          sx={{ width: "fit-content" }}
                          disabled={isMaxContacts}
                          variant='outlined'
                          color='text'
                          size='small'
                          type='button'
                          startIcon={<AppIcon of='add' />}
                          onClick={() => {
                            setFieldValue("contacts", [
                              ...(values.contacts || []),
                              cast<ContactValue>({
                                tempId: IdHelper.newUuid4(),
                                isPrimary: values.contacts?.length === 0,
                              }),
                            ]);
                          }}
                        >
                          Add contact
                        </Button>

                        {isMaxContacts && (
                          <AppIcon
                            sx={{ ml: 1 }}
                            of='info'
                            // color='warning'
                            fontSize='small'
                            inText
                            tooltipTitle={<>Max {CustomerHelper.maxContacts} contacts allowed.</>}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Optional fields */}
                    <FoldableBlock
                      sx={{ mt: 1 }}
                      defaultIsFolded={!defaultValues?.address && !defaultValues?.notes}
                      trigger={{
                        label: <Typography variant='h6'>Other</Typography>,
                      }}
                    >
                      <Box sx={{ my: 1 }}>
                        <Typography component='div' sx={{ mb: 1, fontWeight: "bold" }}>
                          Address
                        </Typography>

                        <GeneralAddressInput
                          value={values.address}
                          errors={getIn(errors, "address")}
                          touched={getIn(touched, "address")}
                          inputsProps={{ all: {} }}
                          onChange={(newValue) => setFieldValue("address", newValue)}
                        />
                      </Box>

                      <AppTextArea
                        error={Boolean(touched.notes && errors.notes)}
                        fullWidth
                        helperText={touched.notes && errors.notes}
                        mode='notes'
                        margin='normal'
                        name='notes'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.notes}
                        variant='outlined'
                      />
                    </FoldableBlock>
                  </FormContentBlock>

                  <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                  <FormActions>
                    <Button
                      color='primary'
                      loading={isSubmitting}
                      type='submit'
                      variant='contained'
                    >
                      Save
                    </Button>
                  </FormActions>
                </form>
              );
            }}
          </Formik>
        </Stack>
      </BaseEntityCreateUpdate>
    );
  },
);
